<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import FileMenu from '../files/FileMenu.vue';
import AddPanel from '../AddPanel.vue';
import { useAuth } from '../../composables/useAuth.js';
import { useConfirm } from '../../composables/useConfirm.js';
import { useFileActions } from '../../composables/useFileActions.js';
import { fmtDate, sortByDate } from '../../domain/format.js';
import { todayIso } from '../../domain/birthDate.js';
import DateField from '../DateField.vue';

const props = defineProps({
  player: { type: Object, required: true },
  trip: { type: Object, required: true },
});
const emit = defineEmits(['remove']);

const { can, canManage, ownerStamp, displayName } = useAuth();
const { confirm } = useConfirm();
const { t } = useI18n();
const { upload, remove, busy } = useFileActions();

const editing = ref(false);
const updatesOpen = ref(false);
const newUpdate = ref('');
const fileInput = ref(null);
const uploadSegment = ref(null);

const manage = computed(() => canManage(props.trip, 'travel'));
const updates = computed(() => props.trip.updates || []);

/* Eine Reise kann mit einer Einladung verknüpft sein — dann liegt das
   Einladungsschreiben hier gleich mit bei. */
const connectedInvitation = computed(() =>
  props.trip.invitationLabel
    ? (props.player.invitations || []).find((x) => x.label === props.trip.invitationLabel)
    : null
);

const segments = computed(() => [
  {
    key: 'out',
    icon: '✈',
    iconClass: 'out',
    title: 'travel.outbound',
    data: props.trip.outbound,
    attachable: true,
  },
  {
    key: 'ret',
    icon: '✈',
    iconClass: 'ret',
    title: 'travel.return',
    data: props.trip.returnFlight,
    attachable: true,
  },
]);

function segmentDetail(seg) {
  const d = seg.data;
  if (!d || !d.from) return null;
  const route = `${d.from} → ${d.to || '—'}`;
  return d.date ? `${route} · ${fmtDate(d.date)}` : route;
}

function pickFile(segKey) {
  uploadSegment.value = segKey;
  fileInput.value.click();
}

async function onPick(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  const segKey = uploadSegment.value;
  uploadSegment.value = null;
  if (!file || !segKey) return;

  const stored = await upload(file, `${segKey}/${props.player.id}`);
  const target = segKey === 'out' ? props.trip.outbound : props.trip.returnFlight;
  if (!target.files) target.files = [];
  target.files.push(stored);
}

function removeSegmentFile(seg, i) {
  const [gone] = seg.data.files.splice(i, 1);
  if (gone) remove(gone);
}

function addUpdate({ close }) {
  const text = newUpdate.value.trim();
  if (!text) return;
  if (!props.trip.updates) props.trip.updates = [];
  props.trip.updates.push({
    text,
    date: todayIso(), // lokales Datum; toISOString wäre UTC und abends in Zeitzonen westlich von UTC schon der nächste Tag
    author: displayName.value,
    ...ownerStamp(),
  });
  newUpdate.value = '';
  updatesOpen.value = true;
  close();
}

async function removeUpdate(index) {
  const ok = await confirm({
    title: t('confirm.deleteUpdateTitle'),
    message: t('confirm.deleteUpdate'),
  });
  if (ok) props.trip.updates.splice(index, 1);
}
</script>

<template>
  <div class="trip-card">
    <!-- ---------- Bearbeiten ---------- -->
    <div v-if="editing && manage" class="entry-edit">
      <div class="er-row">
        <input v-model="trip.label" type="text" :placeholder="$t('travel.title')" class="grow" />
      </div>

      <div class="er-row">
        <label>{{ $t('travel.outbound') }}</label>
        <input v-model="trip.outbound.from" type="text" :placeholder="$t('travel.from')" />
        <input v-model="trip.outbound.to" type="text" :placeholder="$t('travel.to')" />
        <DateField v-model="trip.outbound.date" />
      </div>

      <div class="er-row">
        <label>{{ $t('travel.return') }}</label>
        <input v-model="trip.returnFlight.from" type="text" :placeholder="$t('travel.from')" />
        <input v-model="trip.returnFlight.to" type="text" :placeholder="$t('travel.to')" />
        <DateField v-model="trip.returnFlight.date" />
      </div>

      <div class="er-row">
        <label>{{ $t('travel.accommodation') }}</label>
        <input v-model="trip.hotel" type="text" :placeholder="$t('travel.hotelPlaceholder')" class="grow" />
      </div>

      <div class="er-row">
        <label>{{ $t('travel.connectInvitation') }}</label>
        <select v-model="trip.invitationLabel">
          <option value="">{{ $t('travel.noInvitation') }}</option>
          <option v-for="inv in player.invitations || []" :key="inv.label" :value="inv.label">
            {{ inv.label }}
          </option>
        </select>
      </div>

      <div class="er-row end">
        <button class="btn small" @click="editing = false">{{ $t('common.save') }}</button>
      </div>
    </div>

    <!-- ---------- Ansicht ---------- -->
    <template v-else>
      <div class="trip-card-head">
        <span class="tc-label">{{ trip.label || $t('travel.untitled') }}</span>
        <span v-if="updates.length" class="trip-update-badge">
          {{ $t('travel.updateCount', updates.length, { count: updates.length }) }}
        </span>
        <template v-if="manage">
          <button class="icon-btn tiny" @click="editing = true">{{ $t('common.edit') }}</button>
          <button class="icon-btn tiny" @click="emit('remove')">{{ $t('common.delete') }}</button>
        </template>
      </div>

      <div v-if="trip.invitationLabel" class="inv-connect">
        <span class="inv-connected">🔗 {{ trip.invitationLabel }}</span>
        <FileMenu
          v-if="connectedInvitation?.file"
          :file="connectedInvitation.file"
          :label="$t('travel.viewInvitation')"
          :name="trip.invitationLabel"
          area="travel"
          :removable="false"
        />
      </div>

      <div v-for="seg in segments" :key="seg.key" class="trip-segment">
        <div class="seg-icon" :class="seg.iconClass">{{ seg.icon }}</div>
        <div class="seg-body">
          <span class="seg-title">{{ $t(seg.title) }}</span>
          <div class="seg-detail">
            {{ segmentDetail(seg) || $t('travel.notEntered') }}
          </div>
          <div class="seg-attach">
            <div v-if="seg.data?.files?.length" class="seg-attach-group">
              <FileMenu
                v-for="(f, i) in seg.data.files"
                :key="i"
                :file="f"
                :label="$t('travel.ticketN', { n: i + 1 })"
                :name="trip.label"
                area="travel"
                @remove="removeSegmentFile(seg, i)"
              />
            </div>
            <button
              v-if="manage"
              class="file-label"
              :class="{ ghost: seg.data?.files?.length }"
              :disabled="busy"
              @click="pickFile(seg.key)"
            >
              {{ busy ? $t('common.loading') : (seg.data?.files?.length ? $t('travel.addMoreTicket') : $t('travel.addTicket')) }}
            </button>
          </div>
        </div>
      </div>

      <div class="trip-segment">
        <div class="seg-icon hotel">🏨</div>
        <div class="seg-body">
          <span class="seg-title">{{ $t('travel.accommodation') }}</span>
          <div class="seg-detail">{{ trip.hotel || $t('travel.notEntered') }}</div>
        </div>
      </div>

      <div v-if="trip.createdByName" class="added-by">
        {{ $t('travel.addedBy', { name: trip.createdByName }) }}
      </div>

      <!-- ---------- Meldungen zur Reise ---------- -->
      <div class="trip-updates">
        <div class="trip-updates-head" @click="updatesOpen = !updatesOpen">
          <span>
            🔔 {{ $t('travel.updates') }}
            <template v-if="updates.length">({{ updates.length }})</template>
          </span>
          <span class="mono chevron">{{ updatesOpen ? '▾' : '▸' }}</span>
        </div>

        <div v-if="updatesOpen" class="trip-updates-body">
          <div v-if="!updates.length" class="feed-empty tight">{{ $t('travel.noUpdates') }}</div>

          <div
            v-for="u in sortByDate(updates, 'date')"
            :key="updates.indexOf(u)"
            class="trip-update-item"
          >
            <div class="tu-meta">
              <b>{{ u.author || $t('travel.unknownAuthor') }}</b> · {{ fmtDate(u.date) }}
              <button
                v-if="canManage(u, 'travel')"
                class="feed-edit-btn"
                @click="removeUpdate(updates.indexOf(u))"
              >
                {{ $t('common.delete') }}
              </button>
            </div>
            <div class="tu-text">{{ u.text }}</div>
          </div>

          <AddPanel
            v-if="can('travel', 'write')"
            :label="$t('travel.addUpdate')"
            :submit-label="$t('travel.postUpdate')"
            :invalid="!newUpdate.trim()"
            @submit="addUpdate"
            @cancel="newUpdate = ''"
          >
            <textarea
              v-model="newUpdate"
              rows="3"
              :placeholder="$t('travel.updatePlaceholder')"
            ></textarea>
          </AddPanel>
        </div>
      </div>
    </template>

    <input ref="fileInput" type="file" accept="application/pdf,image/*" hidden @change="onPick" />
  </div>
</template>

<style scoped>
.entry-edit {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.er-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}
.er-row.end {
  justify-content: flex-end;
}
.er-row .grow {
  flex: 1;
  min-width: 180px;
}
.icon-btn.tiny {
  padding: 3px 8px;
  font-size: var(--fs-2xs);
}
.seg-attach {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}
.seg-attach-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
}
.file-label {
  background: none;
  font: inherit;
}
.file-label.ghost {
  opacity: 0.6;
  font-size: var(--fs-2xs);
  padding: 2px 6px;
}
.file-label.ghost:hover {
  opacity: 1;
}
.chevron {
  font-size: var(--fs-2xs);
}
.feed-empty.tight {
  padding: 8px;
}
.added-by {
  padding: 0 4px;
}
textarea {
  width: 100%;
  resize: vertical;
}
</style>
